import {
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faBan,
    faCheckCircle,
    faCheckToSlot,
    faEye,
    faMoneyBills,
    faSearch,
    faSpinner,
    faStopwatch,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UseQueryResult } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { JsonRpcError } from '~/types'
import { useWithdrawRequestsSummary, useWithdrawUserRequestsSummary, useWithdrawVerdictsFor } from '../api'
import { useWithdrawRequests } from '../api/useWithdrawRequests'
import { useWithdrawUserRequests } from '../api/useWithdrawUserRequests'
import {
    WithdrawRequestListResponse,
    WithdrawRequestSummary,
    WithdrawRequest as WithdrawRequestType,
    WithdrawRequestsParams,
} from '../types'
import { WithdrawRequest } from './WithdrawRequest'

type WithdrawRequestListContextType = {
    page: number
    pages: number
    isFetching: boolean
    /** First load, nothing to show yet. */
    isLoading: boolean
    /** The list request failed. Without this a timeout rendered as "No withdraw
     *  requests found" while 28 were waiting. */
    isError: boolean
    data: WithdrawRequestType[]
    setPage: React.Dispatch<React.SetStateAction<number>>
    nextPage: () => void
    prevPage: () => void
    filters: WithdrawRequestsParams['filters']
    setFilters: React.Dispatch<React.SetStateAction<WithdrawRequestsParams['filters']>>
    pageSize: number
    setPageSize: React.Dispatch<React.SetStateAction<number>>
    /** What the verdict filter managed to look at, so the row of controls can
     *  say so rather than letting a capped scan read as the whole queue. */
    verdictScanned?: number | null
    verdictTruncated?: boolean
    verdictUnavailable?: boolean
}

export const WithdrawRequestListContext = createContext<WithdrawRequestListContextType>({
    page: 1,
    pages: undefined,
    isFetching: false,
    isLoading: false,
    isError: false,
    setPage: undefined,
    data: undefined,
    nextPage: undefined,
    prevPage: undefined,
    filters: {},
    setFilters: undefined,
    pageSize: 10,
    setPageSize: undefined,
    verdictScanned: null,
    verdictTruncated: false,
    verdictUnavailable: false,
})

type WithdrawRequestListProviderProps = {
    apiHook: ({ page, page_size, filters }) => UseQueryResult<WithdrawRequestListResponse, JsonRpcError>
    fraudOnly?: boolean
    children: React.ReactNode
}

/* The verdict dropdown is gone.
 *
 * It did the same job as the three chips above the list and did it worse: a
 * select has to be opened to find out what is in it, and the chips carry
 * their counts on their face. Two controls for one filter is how a queue ends
 * up in a state nobody meant to put it in.
 */


const Navigation = () => {
    const { page, pages, isFetching, nextPage, prevPage, pageSize, setPageSize } =
        useContext(WithdrawRequestListContext)
    return (
        <div className='flex flex-row items-center justify-between gap-2'>
            <label>
                <span>page size: </span>
                <input
                    type='number'
                    min={1}
                    max={100}
                    value={pageSize}
                    onChange={(e) => {
                        const value = parseInt(e.target.value)
                        if (value > 0) setPageSize(value)
                    }}
                    className='input-team-tools w-16'
                    placeholder='Page Size'
                />
            </label>
            {pages > 0 && (
                <div className='flex flex-row justify-between items-center gap-2 select-none'>
                    <FontAwesomeIcon icon={faSpinner} spin className={twMerge(isFetching ? 'block' : 'hidden')} />
                    <button
                        onClick={prevPage}
                        disabled={page == 1}
                        className='border rounded p-1 border-white/10 enabled:hover:border-white/50 disabled:opacity-30'
                    >
                        <small>
                            <FontAwesomeIcon icon={faAngleDoubleLeft} />
                        </small>
                    </button>
                    <span>
                        {page} / {pages}
                    </span>
                    <button
                        onClick={nextPage}
                        disabled={page == pages}
                        className='border rounded p-1 border-white/10 enabled:hover:border-white/50 disabled:opacity-30'
                    >
                        <small>
                            <FontAwesomeIcon icon={faAngleDoubleRight} />
                        </small>
                    </button>
                </div>
            )}
        </div>
    )
}

const WithdrawRequestList = ({ apiHook, children, fraudOnly = false }: WithdrawRequestListProviderProps) => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const [searchParams, setSearchParams] = useSearchParams()
    const status = searchParams.get('status')
    const username = searchParams.get('username')
    // In the URL alongside status, so "the routine ones still waiting" is a link
    // somebody can send to whoever is on shift next.
    const verdict = searchParams.get('verdict') as WithdrawRequestsParams['filters']['verdict']
    // `fraud` lives in the same filter state as status and username rather than
    // being merged in at the call site, so the summary counts above the list
    // agree with the list itself. Both read this one object.
    const [filters, setFilters] = useState<WithdrawRequestsParams['filters']>({
        status: status ?? 'all',
        username,
        verdict: verdict ?? 'all',
        fraud: fraudOnly || undefined,
    })

    useEffect(() => {
        if (filters?.status != status || filters?.username != username || filters?.verdict != verdict)
            setSearchParams(
                Object.fromEntries(
                    Object.entries(filters)
                        // `fraud` is driven by a checkbox on the page, not by the URL,
                        // and it is a boolean -- setSearchParams only takes strings.
                        .filter(([key, value]) => key !== 'fraud' && value && value != 'all')
                        .map(([key, value]) => [key, String(value)])
                )
            )
        setPage(1)
    }, [filters])

    useEffect(() => {
        const status = searchParams.get('status')
        const username = searchParams.get('username')
        // fraudOnly is a prop rather than a search param, so it has to be
        // re-applied here or this effect silently drops it every time the URL
        // changes.
        const verdict = searchParams.get('verdict') as WithdrawRequestsParams['filters']['verdict']
        setFilters({
            status: status ?? 'all',
            username,
            verdict: verdict ?? 'all',
            fraud: fraudOnly || undefined,
        })
    }, [searchParams, fraudOnly])

    const { data, isFetching, isLoading, isError } = apiHook({ page, page_size: pageSize, filters })
    const hasNextPage = data?.pages > page
    const hasPrevPage = page > 1
    const nextPage = () => hasNextPage && setPage((page) => page + 1)
    const prevPage = () => hasPrevPage && setPage((page) => page - 1)
    return (
        <div>
            <WithdrawRequestListContext.Provider
                value={{
                    data: data?.data ?? [],
                    page: page,
                    setPage,
                    pages: data?.pages,
                    isFetching,
                    isLoading,
                    isError,
                    nextPage,
                    prevPage,
                    filters,
                    setFilters,
                    pageSize,
                    setPageSize,
                    verdictScanned: data?.verdict_scanned ?? null,
                    verdictTruncated: data?.verdict_truncated ?? false,
                    verdictUnavailable: data?.verdict_unavailable ?? false,
                }}
            >
                <div className='flex flex-col gap-1'>{children}</div>
            </WithdrawRequestListContext.Provider>
        </div>
    )
}

/** What each counter counts, and what clicking it does.
 *
 *  They were an icon and a number with the raw status key as the tooltip, so
 *  a row of six told you six things you could not name. On a payments screen
 *  the difference between "approved" and "paid" is whether the money has left,
 *  which is not a thing to leave to a guess about an icon. */
const SUMMARY_LABEL: Record<string, { label: string; title: string }> = {
    all: { label: 'all', title: 'Every withdrawal request, whatever state it is in' },
    created: { label: 'waiting', title: 'Requested, and nobody has decided yet' },
    submitted: { label: 'waiting', title: 'Requested, and nobody has decided yet' },
    approved: { label: 'approved', title: 'Approved, but the money has not been sent yet' },
    declined: { label: 'declined', title: 'Declined, whether refunded or held' },
}

/** The three verdicts, counted and clickable.
 *
 *  "Sending" and "paid" are states nobody sorts by -- the money has already
 *  gone and there is no decision left in either. What a reviewer picks up the
 *  queue to find is which rows are easy, which need one look, and which must
 *  not be paid, so those are the counters.
 *
 *  Counted over the whole WAITING queue by the server, not over the rows on
 *  this page. Counting the page put "6 looks routine, 0 needs review, 4 don't
 *  pay yet" in a row beside "29 waiting", which is a page contradicting
 *  itself: three numbers summing to the page size, sitting among running
 *  totals, with the word "page" nowhere on them. A number next to a total has
 *  to be one.
 */
const VERDICT_CHIPS = [
    {
        key: 'cleared' as const,
        label: 'looks routine',
        icon: faCheckCircle,
        tone: 'text-green-500 border-green-500/40',
        on: 'border-green-500 bg-green-500/10',
    },
    {
        key: 'review' as const,
        label: 'needs review',
        icon: faEye,
        tone: 'text-amber-400 border-amber-400/40',
        on: 'border-amber-400 bg-amber-400/10',
    },
    {
        key: 'hold' as const,
        label: "don't pay yet",
        icon: faStopwatch,
        tone: 'text-red-500 border-red-500/40',
        on: 'border-red-500 bg-red-500/10',
    },
]

const VerdictChips = ({ summary }: { summary: WithdrawRequestSummary }) => {
    const { filters, setFilters } = useContext(WithdrawRequestListContext)

    return (
        <>
            {VERDICT_CHIPS.map((chip) => (
                <button
                    key={chip.key}
                    title={
                        `${chip.label} — ${summary[chip.key] ?? 0} of the ${summary.created} waiting` +
                        (summary.verdict_truncated
                            ? ', counted over the oldest part of the queue'
                            : '') +
                        '. Click to show only these.'
                    }
                    onClick={() =>
                        setFilters((prev) => ({
                            ...prev,
                            verdict: prev?.verdict === chip.key ? 'all' : chip.key,
                        }))
                    }
                    className={twMerge(
                        'flex flex-row items-center gap-1.5 rounded border px-2 py-1',
                        chip.tone,
                        filters?.verdict === chip.key && chip.on
                    )}
                >
                    <FontAwesomeIcon icon={chip.icon} />
                    <span className='font-bold tabular-nums'>{summary[chip.key] ?? 0}</span>
                    <span className='font-normal uppercase tracking-wide opacity-70'>{chip.label}</span>
                </button>
            ))}
            {/* Said out loud rather than left to a tooltip. If the three do
                not add up to the waiting count, the reason is on screen --
                which is the whole complaint these chips started with. */}
            {summary.verdict_truncated && (
                <span className='text-white/40' title='The waiting queue is longer than the triage scan cap'>
                    of the oldest {summary.cleared + summary.review + summary.hold}
                </span>
            )}
        </>
    )
}

/* `verdicts` is OFF by default, and that is the point.
 *
 * This component is shared by the staff queue and by a player's own
 * withdrawals page, and it rendered the triage chips unconditionally -- so
 * "looks routine / needs review / DON'T PAY YET" appeared on the player's
 * page, under their own Request Withdrawal button.
 *
 * Nothing of anyone else's leaked: the counts need
 * `bank.withdraw_request.verdicts`, which is MarketingRole-only and refused
 * the call, so the chips sat at zero and every player's page fired a staff
 * endpoint that 403'd on load. What did leak is the vocabulary of an internal
 * review process onto the screen of the person being reviewed.
 *
 * Defaulting to off rather than passing `false` from the player template: the
 * next caller of this component gets the safe behaviour without having to know
 * this happened. */
const WithdrawRequestsSummary = ({ apiHook, verdicts = false }) => {
    const { filters, setFilters } = useContext(WithdrawRequestListContext)
    const { data } = apiHook({ filters })

    return (
        data && (
            <div className='flex flex-row items-center justify-center gap-4 text-xs'>
                {Object.entries(data)
                    .filter(([key]) => key in SUMMARY_LABEL)
                    .map(([key, value]: [keyof WithdrawRequestSummary, any]) => (
                    <button
                        key={key}
                        title={SUMMARY_LABEL[key]?.title ?? key}
                        onClick={() => setFilters((prev) => ({ ...prev, status: key }))}
                        className={twMerge(
                            'flex flex-row items-center gap-1.5 px-2 py-1',
                            'border border-white/15 rounded hover:border-white/50',
                            key == filters?.status && 'border-white',
                            {
                                all: 'text-white',
                                created: 'text-white',
                                submitted: 'text-white',
                                approved: 'text-blue-500',
                                declined: 'text-red-400',
                            }[key]
                        )}
                    >
                        <FontAwesomeIcon
                            icon={
                                {
                                    all: faMoneyBills,
                                    created: faEye,
                                    submitted: faEye,
                                    approved: faCheckToSlot,
                                    declined: faBan,
                                }[key]
                            }
                        />
                        <div className='font-bold tabular-nums'>{value}</div>
                        <div className='font-normal uppercase tracking-wide opacity-70'>
                            {SUMMARY_LABEL[key]?.label ?? key}
                        </div>
                    </button>
                    ))}
                {/* Staff only. The player's summary is a different DTO with
                    no verdicts in it, and a player has no business reading the
                    triage on their own withdrawal anyway -- these rendered as
                    three zeroes on that page. */}
                {verdicts && <VerdictChips summary={data} />}
            </div>
        )
    )
}

const VERDICT_RANK = { hold: 0, review: 1, cleared: 2 }

/** What to say when there are no rows, so a failure never reads as an empty queue. */
const ListEmptyState = () => {
    const { isLoading, isError } = useContext(WithdrawRequestListContext)
    if (isLoading)
        return <small className='text-white/50 text-center font-bold uppercase py-4'>Loading withdraw requests…</small>
    if (isError)
        return (
            <small className='text-red-400 text-center font-bold uppercase py-4'>
                Could not load withdraw requests. Refresh to try again.
            </small>
        )
    return <small className='text-white/50 text-center font-bold uppercase py-4'>No withdraw requests found</small>
}

const WithdrawRequestListAdminTemplate = () => {
    const { data, filters } = useContext(WithdrawRequestListContext)

    // Worst first, but only within the page the server sent -- the ordering is
    // still `-id` on the backend and this does not reach across pages. Rows
    // without a triage keep their original position relative to each other.
    // Verdicts now load per row on expand, so on a fresh page this is a no-op
    // and only the verdict filter, which computes them server-side, sorts.
    /* Every row's verdict, in one call, once the list is on screen.
     *
     * A queue exists to say which rows need somebody. A row that shows no
     * verdict, no score and no history until it is opened cannot do that, so
     * the pills are fetched for the whole page rather than per row on expand.
     * It is the same triage work either way; this is one request instead of
     * ten, and it runs after the rows are already drawn. */
    const uuids = (data ?? []).map((row) => row.uuid)
    const batch = useWithdrawVerdictsFor(uuids, uuids.length > 0)

    const merged = (data ?? []).map((row) => {
        const found = batch.data?.[row.uuid]
        if (found) return { ...row, ...found }
        // Still working: say so on the row rather than leaving a gap that
        // reads as "nothing to report".
        // A failed batch is said on the row, not left as a gap that reads as
        // "nothing to report" (B-c14).
        return batch.isError ? { ...row, checks_failed: !row.triage } : { ...row, verdict_pending: !row.triage }
    })

    /* Filtered to what is waiting, this is a queue, and a queue is ordered by
     * how long somebody has been in it -- oldest first, because the person who
     * asked on Tuesday has been waiting since Tuesday. Unfiltered, the worst
     * verdict leads, because then the question is which rows need somebody at
     * all rather than who has waited longest. */
    const waitingOnly = filters?.status === 'created' || filters?.status === 'submitted'

    /* The waiting order is the SERVER's now (`order_by: 'oldest'`), because
     * sorting here only ever reached the rows on this page -- so the heading
     * said "longest first" over the ten newest requests, sorted among
     * themselves, while the oldest sat on page two. Re-sorting here as well
     * would be a second opinion about the same thing. */
    const rows = waitingOnly
        ? merged
        : [...merged].sort(
              (a, b) =>
                  (VERDICT_RANK[a.triage?.verdict] ?? Number.MAX_SAFE_INTEGER) -
                  (VERDICT_RANK[b.triage?.verdict] ?? Number.MAX_SAFE_INTEGER)
          )

    /* One flat list could not answer "what needs me now".
     *
     * Four states, and the order is the order somebody works in: what the
     * server will not pay without an override, what a check flagged, what is
     * merely waiting, and what is already settled and is here for reference.
     *
     * Grouped on what every row actually carries -- its status and its payout
     * block. The verdict does not qualify: it loads per row on expand, so on a
     * fresh page most rows have none, and a row whose checks have not run is
     * NOT the same as a row that passed them. Those sit in "waiting", and the
     * heading says so rather than implying they were cleared.
     */
    const bucket = (row: WithdrawRequestType) => {
        if (row.status !== 'requested') return 'decided'
        if (row.payout_block?.blocked) return 'blocked'
        if (row.triage && row.triage.verdict !== 'cleared') return 'flagged'
        return 'waiting'
    }

    const GROUPS: Array<{ key: string; title: string; hint: string; tone: string }> = [
        {
            key: 'blocked',
            title: 'The server will not pay these',
            hint: 'Refused outright. Paying one takes a written reason.',
            tone: 'text-red-400 border-red-400/40',
        },
        {
            key: 'flagged',
            title: 'A check flagged these',
            hint: 'Advice, not a refusal — you can still pay them.',
            tone: 'text-amber-400 border-amber-400/40',
        },
        {
            key: 'waiting',
            title: 'Waiting on you',
            hint: 'Nothing has refused these. Their checks run when you open one.',
            tone: 'text-white/50 border-white/15',
        },
        {
            key: 'decided',
            title: 'Already decided',
            hint: 'Here for reference. No decision left to make.',
            tone: 'text-white/30 border-white/10',
        },
    ]

    if (!rows?.length) {
        return (
            <div className='flex flex-col text-xs'>
                <ListEmptyState />
            </div>
        )
    }

    /* Asked for "everything still waiting", the answer is one list.
     *
     * The groups exist to answer "what needs me now" out of a mixed queue.
     * Once the queue has been filtered to exactly that, splitting it again
     * only hides where the oldest request is -- which is the thing this view
     * is for. */
    if (waitingOnly) {
        return (
            <div className='flex flex-col text-xs'>
                <div className='flex flex-row flex-wrap items-baseline gap-x-3 border-b border-white/15 pb-1 text-white/50'>
                    <span className='text-[10px] font-bold uppercase tracking-widest'>
                        Waiting, longest first
                    </span>
                    <span className='text-[10px] tabular-nums opacity-70'>{rows.length}</span>
                </div>
                {rows.map((withdrawRequest) => (
                    <WithdrawRequest key={withdrawRequest.uuid} withdrawRequest={withdrawRequest}>
                        <WithdrawRequest.AdminTemplate />
                    </WithdrawRequest>
                ))}
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-4 text-xs'>
            {GROUPS.map((group) => {
                const inGroup = rows.filter((row) => bucket(row) === group.key)
                if (!inGroup.length) return null
                return (
                    <div key={group.key} className='flex flex-col'>
                        <div
                            className={`flex flex-row flex-wrap items-baseline gap-x-3 border-b pb-1 ${group.tone}`}
                        >
                            <span className='text-[10px] font-bold uppercase tracking-widest'>
                                {group.title}
                            </span>
                            <span className='text-[10px] tabular-nums opacity-70'>{inGroup.length}</span>
                            <span className='text-[10px] font-normal text-white/30'>{group.hint}</span>
                        </div>
                        {inGroup.map((withdrawRequest) => (
                            <WithdrawRequest key={withdrawRequest.uuid} withdrawRequest={withdrawRequest}>
                                <WithdrawRequest.AdminTemplate />
                            </WithdrawRequest>
                        ))}
                    </div>
                )
            })}
        </div>
    )
}

const WithdrawRequestListUserTemplate = () => {
    const { data } = useContext(WithdrawRequestListContext)
    return (
        <div className='flex flex-col gap-4 text-xs'>
            {data?.length > 0 ? (
                data?.map((withdrawRequest) => (
                    <WithdrawRequest key={withdrawRequest.uuid} withdrawRequest={withdrawRequest}>
                        <WithdrawRequest.UserTemplate />
                    </WithdrawRequest>
                ))
            ) : (
                <ListEmptyState />
            )}
        </div>
    )
}

const WithdrawUsernameFilter = () => {
    const { filters, setFilters } = useContext(WithdrawRequestListContext)
    const [username, setUsername] = useState(filters?.username ?? '')

    const submit = (e) => {
        e.preventDefault()
        setFilters({ ...filters, username: username || null })
    }

    useEffect(() => setUsername(filters?.username ?? ''), [filters])

    return (
        <form className='flex flex-row gap-2' onSubmit={submit}>
            <input
                type='text'
                placeholder='Username'
                className='input-team-tools my-2'
                value={username ?? ''}
                onChange={(e) => setUsername(e.target.value)}
            />
            <button className='btn-team-tools'>
                <FontAwesomeIcon icon={faSearch} />
            </button>
        </form>
    )
}

WithdrawRequestList.AdminTemplate = ({ fraudOnly = false }: { fraudOnly?: boolean }) => {
    return (
        <div className='flex flex-col'>
            <WithdrawRequestList apiHook={useWithdrawRequests} fraudOnly={fraudOnly}>
                <WithdrawRequestsSummary apiHook={useWithdrawRequestsSummary} verdicts />
                <WithdrawUsernameFilter />
                <small className='flex flex-row self-end'>
                    <Navigation />
                </small>
                <WithdrawRequestListAdminTemplate />
                <small className='flex flex-row self-end my-4'>
                    <Navigation />
                </small>
            </WithdrawRequestList>
        </div>
    )
}

WithdrawRequestList.UserTemplate = () => (
    <WithdrawRequestList apiHook={useWithdrawUserRequests}>
        <WithdrawRequestsSummary apiHook={useWithdrawUserRequestsSummary} />
        <small className='flex flex-row self-end'>
            <Navigation />
        </small>
        <WithdrawRequestListUserTemplate />
        <small className='flex flex-row self-end my-4'>
            <Navigation />
        </small>
    </WithdrawRequestList>
)

export { WithdrawRequestList }
